import React, { createContext, useContext, useState, useCallback } from 'react';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';

const ClusterDataContext = createContext();

export const useClusterData = () => {
  const context = useContext(ClusterDataContext);
  if (!context) {
    throw new Error('useClusterData must be used within a ClusterDataProvider');
  }
  return context;
};

export const ClusterDataProvider = ({ stackName, clusterId, children }) => {
  const [refreshingComponents, setRefreshingComponents] = useState(new Set());

  const { data, error, refetch: originalRefetch } = useSdpdsReadOnlyOperation({
    operation_name: 'DescribeCluster',
    payload: {
      cluster: {
        stackName,
        clusterId,
      },
    }
  });

  const clusterData = data?.sdpdsReadOnlyOperation?.clusterDescription;
  const isInitialLoading = !data && !error;

  const refetch = useCallback(async (componentId) => {
    setRefreshingComponents(prev => new Set([...prev, componentId]));
    try {
      const result = await originalRefetch();
      return result;
    } catch (err) {
      console.error('Refetch failed:', err);
      throw err;
    } finally {
      setRefreshingComponents(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(componentId);
        return newSet;
      });
    }
  }, [originalRefetch]);

  const isComponentRefreshing = useCallback((componentId) => {
    return refreshingComponents.has(componentId);
  }, [refreshingComponents]);

  const contextValue = {
    clusterData,
    error,
    isInitialLoading,
    refetch,
    isComponentRefreshing,
  };

  return (
    <ClusterDataContext.Provider value={contextValue}>
      {children}
    </ClusterDataContext.Provider>
  );
};